<template>
  <div class="f-v">
    <donut-modal-sections-title class="b-b1 bc-lighter-gray" :title="title" :show-dismiss="true" />
    <div class="flex h-400">
      <div class="f-v f-gap-8 w-min-280 w-max-280 p-14 bg-form">
        <div v-for="category in enrollmentCategories" :key="`category-${category.id}`" class="c-pointer category-list-item" :class="{ 'active': category.id === selectedCategory.id }" @click.prevent="selectCategory(category)">
          <a class="f p-t14 p-r14 p-l14 br-8"><i class="mi m-r" :class="category.iconClass">{{ category.icon }}</i><div class="t">{{ category.title }}</div></a>
          <div class="f t-sm gray p-b14 p-r14 p-l14">{{ category.description }}</div>
        </div>
      </div>
      <div class="f-v scroll-y-auto scroll-shadows item-list h-max-400">
        <div :id="selectedCategory.id" class="item-list-section">
          <div v-for="(item, itemIdx) in selectedCategory.items" :key="`category-${selectedCategory.id}-item-${itemIdx}`" v-tooltip.top="item.disabled ? item.tooltip : null">
            <a class="f p-h14 p-v20 b-b1 c-pointer item-list-item" :class="{disabled: item.disabled}" @click="onItemClick(item)">
              <div class="f w-100p">
                <div class="f-v fc-g1 w-min-75p">
                  <div class="f m-b"><i class="mi w-min-24 w-max-24" :class="item.iconClass">{{ item.icon }}</i>
                    <div class="t m-l8 bold">{{ item.title }}</div>
                  </div>
                  <div class="f">
                    <div class="w-min-24 w-max-24">&nbsp;</div>
                    <div class="t m-l8 gray">{{ item.description }}</div>
                  </div>
                </div>
                <div class="f-v f-ae w-x11">
                  <div v-if="item.showBadge" class="hide-on-hover">
                    <div class="t-xs ta-c white bold p-h4 br-4" :class="item.badgeClass">{{ item.badgeText }}</div>
                  </div>
                  <div class="show-on-hover"><i class="mi link md-22">add</i></div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import useClientState from 'composables/client-state';

  const { getLocalStorage, setLocalStorage } = useClientState();
  const { state, commit, dispatch } = useStore();

  const { flowType } = state.modal.data;
  const { tag } = state.onboardingBatches;
  const { currentUser, currentTeam } = state;

  const title = computed(() => `Choose how you would like to enroll ${flowType.primaryRolePlural}`);

  const selectedCategory = ref({});

  const selectCategory = (category) => {
    setLocalStorage('enrollmentCategoryIdx', category.id);
    window.mixpanel.track('Enrollment Modal - Category Selected', { category: category.id });
    selectedCategory.value = category;
  };

  const onItemClick = (item) => {
    if (item.disabled) return;

    if (item.action) {
      dispatch(item.action, item.payload);
    } else if (item.mutation) {
      commit(item.mutation, item.payload);
    } else if (item.href) {
      window.location = item.href;
    }
  };

  const hrisEnrollmentAction = computed(() => {
    if (currentTeam.has_hris_connection) {
      return 'hrisManagePrograms/openModal';
    }
  });

  const manualEnrollmentPath = (type) => {
    let prefix = `/${flowType.routeResource}`;
    let path;
    if (tag?.id) {
      prefix += `/tags/${tag.id}`;
    }

    if (type === 'single') {
      path = `${prefix}/sessions/new`;
    } else if (type === 'bulk') {
      path = `${prefix}/batches/new`;
    } else if (type === 'combined') {
      path = `${prefix}/manual_enrollment`;
    }

    return path;
  };

  const validEnrollmentType = (item) => {
    let valid = true;
    let badge;
    let tooltip;
    if (item.validFlowTypes === 'onboarding') {
      valid = flowType.is_onboarding;
    } else if (item.validFlowTypes === 'notOnboarding') {
      valid = !flowType.is_onboarding;
    }

    if (!valid) {
      badge = 'flowTypeMismatch';
    }

    if (item.adminOnly && valid) {
      valid = currentUser.onboarding_admin || currentUser.onboarding_member;
      if (!valid) tooltip = 'Only Donut account admins and Journeys admins can auto-enroll. Please request admin permissions from an existing admin.';
    }

    if (!valid && !badge) {
      badge = 'adminOnly';
    }

    return { valid, badge, tooltip };
  };

  const badges = {
    flowTypeMismatch: {
      badgeText: 'Unavailable for this Journey',
      badgeClass: 'bg-gray',
    },
    adminOnly: {
      badgeText: 'Admins only',
      badgeClass: 'bg-orange-2',
    },
    recommended: {
      badgeText: 'Recommended',
      badgeClass: 'bg-blue-full',
    },
  };

  const withDisabledAndBadge = (item) => {
    const { valid, badge, tooltip } = validEnrollmentType(item);
    if (!valid) {
      item.disabled = true;
      item.showBadge = true;
      item.badgeClass = badges[badge].badgeClass;
      item.badgeText = badges[badge].badgeText;
      if (tooltip) item.tooltip = tooltip;
    } else if (item.recommended) {
      item.showBadge = true;
      item.badgeClass = badges.recommended.badgeClass;
      item.badgeText = badges.recommended.badgeText;
    }
    return item;
  };

  const autoEnrollmentItems = computed(() => [
    {
      title: `Auto-enroll ${flowType.primaryRolePlural} imported from your HRIS`,
      description: `${flowType.primaryRolePlural} will be imported from your HRIS and will be auto-enrolled in these Journeys. Recommended for onboarding New Hires.`,
      iconClass: 'icon-green',
      icon: 'extension',
      recommended: !currentTeam.has_hris_connection,
      validFlowTypes: 'onboarding',
      tooltip: 'Only available for onboarding Journeys',
      adminOnly: true,
      action: state.modal.data.hasAutoEnrollTags ? null : hrisEnrollmentAction,
      href: state.modal.data.hasAutoEnrollTags ? '/onboarding/tags/enrollment' : '/hris',
      payload: { fromInitialSync: false },
    },
    {
      title: 'Auto-enroll all new Slack users',
      description: 'All new users in your Slack workspace will be enrolled automatically to these selected Journeys. Recommended for onboarding New Hires.',
      iconClass: 'icon-orange',
      icon: 'tag',
      validFlowTypes: 'onboarding',
      tooltip: 'Only available for onboarding Journeys',
      adminOnly: true,
      action: state.modal.data.hasAutoEnrollTags ? null : 'onboardingEnrollmentSlackJoin/openSettingsModal',
      href: '/onboarding/tags/enrollment',
      payload: { tag, flowType },
    },
    {
      title: `Auto-enroll ${flowType.primaryRolePlural} by joining a Slack channel`,
      description: 'Everyone joining a specific Slack channel will be enrolled in these Journeys. Recommended for individual training or Employee Resource Group onboarding.',
      iconClass: 'icon-blue',
      icon: 'people',
      showBadge: true,
      badgeText: 'New',
      badgeClass: 'bg-medium-green',
      action: state.modal.data.hasAutoEnrollTags ? null : 'onboardingEnrollmentChannelJoin/openSettingsModal',
      href: '/onboarding/tags/enrollment',
      adminOnly: true,
      payload: { tag, flowType },
    },
  ].map(item => withDisabledAndBadge(item)));

  const manualEnrollmentItems = computed(() => {
    const actionArgs = {};
    if (state.modal.data.tag) {
      actionArgs.href = manualEnrollmentPath('combined');
    } else {
      actionArgs.mutation = 'updateModal';
      actionArgs.payload = {
        visible: true,
        subcomponent: 'onboarding-tags-enrollment-modals-default-tag',
        size: 'of-v',
        data: { flowType },
      };
    }
    return [
      {
        title: 'Manually',
        description: 'Enroll employees or new hires by entering their Journey start date and other roles involved.',
        iconClass: 'icon-green',
        icon: 'groups',
        ...actionArgs,
      },
      {
        title: `Enroll ${flowType.primaryRolePlural} by answering a form`,
        description: 'Create a form and send it to participants to enroll in this Journey. Recommended for mentorship programs and company events.',
        iconClass: 'icon-blue',
        icon: 'ballot',
        showBadge: true,
        badgeText: 'New',
        badgeClass: 'bg-medium-green',
        recommended: tag?.flow_type === 'mentorship',
        validFlowTypes: 'notOnboarding',
        tooltip: 'Not available for onboarding Journeys',
        href: tag && `/${flowType.routeResource}/tags/${tag?.id}/enrollment_surveys/new`,
        action: !tag && 'onboardingEnrollmentSurvey/openInitialModal',
        payload: { tag, flowType, source: 'enrollment-type-selection' },
      },
    ].map(item => withDisabledAndBadge(item));
  });

  const enrollmentCategories = computed(() => [
    {
      id: 'automatic-enrollment',
      title: 'Ongoing automatic enrollment',
      description: `Enroll ${flowType.primaryRolePlural} automatically as they join your Slack, HRIS or join a channel.`,
      icon: 'loop',
      iconClass: 'icon-red',
      items: autoEnrollmentItems.value,
    },
    {
      id: 'one-time-enrollment',
      title: 'One time enrollment',
      description: `Enroll ${flowType.primaryRolePlural} in a Journey manually every time new cohorts start.`,
      icon: 'person_add',
      iconClass: 'icon-green',
      items: manualEnrollmentItems.value,
    },
  ]);

  onMounted(() => {
    if (getLocalStorage('enrollmentCategoryIdx')) {
      selectedCategory.value = enrollmentCategories.value.find((category) => category.id === getLocalStorage('enrollmentCategoryIdx'));
    } else {
      selectedCategory.value = enrollmentCategories.value[0];
    }
  });
</script>
